import { Box, Spinner } from '@chakra-ui/react';
import {
  VStack,
  Heading,
  Pressable,
  Text,
  OptionRow,
  ArrowCta,
  Link,
} from 'components/ui';
import { useFormData } from '../../utils/formReducer.js';
import { FORM_ACTIONS } from '../../utils/formActions.js';
import { useSymptom } from '../../api/onboarding/useSymptom';
import { useGoogleTagManager } from '../../hooks/useGoogleTagManager';
import React, { useEffect } from 'react';
import { FORM_VIEW, FormViews, TRACKING_ONBOARDING_STARTED } from './constants';
import { Pill } from './Pill';
import HeaderNav from '../HeaderNav/HeaderNav';
import { StickyCtaLayout } from '../ui/StickyCtaLayout';

const GiSymptomsList = ({ onNext }) => {
  const { state: formData, dispatch } = useFormData();
  const { trackEventInGoogleTagManager } = useGoogleTagManager();
  const { data, isLoading } = useSymptom();

  useEffect(() => {
    trackEventInGoogleTagManager(TRACKING_ONBOARDING_STARTED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <Spinner />;

  const primarySymptoms = data?.filter(
    (symptom) => symptom.category === 'Primary'
  );

  const secondaryGiSymptoms = data?.filter(
    (symptom) => symptom.category === 'Secondary'
  );

  const handleSelect = (symptomId) => {
    let symptomCopy = [...formData.symptom_ids];
    if (!symptomCopy.includes(symptomId)) {
      symptomCopy.push(symptomId);
    } else {
      symptomCopy = symptomCopy.filter((id) => id !== symptomId);
    }

    dispatch({
      type: FORM_ACTIONS.UPDATE_FORM_FIELD,
      fieldName: 'symptom_ids',
      payload: symptomCopy,
    });
  };

  const handleSkip = () => {
    dispatch({
      type: FORM_ACTIONS.UPDATE_FORM_FIELD,
      fieldName: 'symptom_ids',
      payload: [],
    });

    onNext();
  };

  const renderContent = () => {
    return (
      <VStack gap={8} w="100%">
        <Heading textStyle="heading.h2" textAlign={'center'}>
          Welcome! What symptoms can we help you manage?
        </Heading>

        <VStack gap="10px" w="100%" align="stretch">
          <Text textStyle="label.section" textAlign="left">
            Select all that apply.
          </Text>

          <VStack gap={2} w="100%">
            {primarySymptoms?.map((symptom) => {
              return (
                <OptionRow
                  key={symptom.id}
                  isSelected={formData.symptom_ids.includes(symptom.id)}
                  onClick={() => handleSelect(symptom.id)}
                >
                  {symptom.symptom}
                </OptionRow>
              );
            })}
          </VStack>
        </VStack>

        <VStack gap="10px" w="100%" align="stretch">
          <Text textStyle="label.section" textAlign="left" w="100%">
            We can also help with these symptoms:
          </Text>

          <Box flexDirection="row" flexWrap="wrap" display="flex" gap="9px">
            {secondaryGiSymptoms?.map((symptom) => {
              return (
                <Pressable
                  key={symptom.id}
                  onClick={() => handleSelect(symptom.id)}
                >
                  <Pill
                    isSelected={formData.symptom_ids.includes(symptom.id)}
                    text={symptom.symptom}
                  />
                </Pressable>
              );
            })}
          </Box>
        </VStack>
      </VStack>
    );
  };

  return (
    <StickyCtaLayout
      actionArea={
        <>
          <ArrowCta disabled={!formData.symptom_ids.length} onClick={onNext}>
            Next
          </ArrowCta>
          <Link
            color="secondary.500"
            fontWeight={600}
            isExternal={false}
            mt={4}
            onClick={handleSkip}
            opacity={isLoading ? 0.5 : 1}
          >
            I don't have any of these symptoms
          </Link>
        </>
      }
      header={
        <HeaderNav
          screenName={FORM_VIEW[FormViews.startOfFlow].trackingLabel}
          showWhatIsAybleModal
        />
      }
      maxWidth="formWidth"
    >
      <VStack
        maxWidth={'100%'}
        w="100%"
        gap={1}
        data-testid={'onboarding-gi-symptoms-list'}
        alignContent={'center'}
      >
        {renderContent()}
      </VStack>
    </StickyCtaLayout>
  );
};

export default GiSymptomsList;
